import React, { useState, useEffect } from 'react';
import { FileText, Save, Globe, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config';

const PagesManagement = ({ token, i18n }) => {
  const [activePage, setActivePage] = useState('privacy-policy');
  const [activeLanguage, setActiveLanguage] = useState('sv');
  const [content, setContent] = useState({
    'privacy-policy': { en: '', sv: '', ar: '' },
    'terms-of-service': { en: '', sv: '', ar: '' },
    'refund-policy': { en: '', sv: '', ar: '' },
    'disclaimer': { en: '', sv: '', ar: '' },
    'cookies-policy': { en: '', sv: '', ar: '' }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const pages = [
    { id: 'privacy-policy', name: i18n.language === 'ar' ? 'سياسة الخصوصية' : i18n.language === 'sv' ? 'Integritetspolicy' : 'Privacy Policy' },
    { id: 'terms-of-service', name: i18n.language === 'ar' ? 'شروط الخدمة' : i18n.language === 'sv' ? 'Användarvillkor' : 'Terms of Service' },
    { id: 'refund-policy', name: i18n.language === 'ar' ? 'سياسة الاسترجاع' : i18n.language === 'sv' ? 'Återbetalningspolicy' : 'Refund Policy' },
    { id: 'disclaimer', name: i18n.language === 'ar' ? 'إخلاء المسؤولية' : i18n.language === 'sv' ? 'Ansvarsfriskrivning' : 'Disclaimer' },
    { id: 'cookies-policy', name: i18n.language === 'ar' ? 'سياسة الكوكيز' : i18n.language === 'sv' ? 'Cookie-policy' : 'Cookies Policy' }
  ];

  const languages = [
    { code: 'sv', name: 'Svenska 🇸🇪' },
    { code: 'en', name: 'English 🇬🇧' },
    { code: 'ar', name: 'العربية 🇸🇦' }
  ];

  const defaultContent = {
    'privacy-policy': {
      en: `# Privacy Policy

## Introduction
Welcome to DriveMaster. We respect your privacy and are committed to protecting your personal data.

## Information We Collect
- **Account Information**: Name, email address, and password when you register.
- **Usage Data**: Information about how you use our services.
- **Payment Information**: Processed securely through Stripe.

## How We Use Your Information
- To provide and maintain our services
- To notify you about changes
- To provide customer support

## Contact Us
drivemaster.se@gmail.com`,
      sv: `# Integritetspolicy

## Introduktion
Välkommen till DriveMaster. Vi respekterar din integritet och är engagerade i att skydda dina personuppgifter.

## Information vi samlar in
- **Kontoinformation**: Namn, e-post och lösenord.
- **Användningsdata**: Information om hur du använder tjänsten.
- **Betalningsinformation**: Behandlas säkert via Stripe.

## Hur vi använder din information
- För att tillhandahålla våra tjänster
- För att meddela dig om ändringar
- För att ge kundsupport

## Kontakta oss
drivemaster.se@gmail.com`,
      ar: `# سياسة الخصوصية

## مقدمة
مرحباً بك في DriveMaster. نحن نحترم خصوصيتك ونلتزم بحماية بياناتك.

## المعلومات التي نجمعها
- **معلومات الحساب**: الاسم والبريد الإلكتروني وكلمة المرور.
- **بيانات الاستخدام**: معلومات حول استخدامك للخدمة.
- **معلومات الدفع**: تتم معالجتها بشكل آمن عبر Stripe.

## كيف نستخدم معلوماتك
- لتقديم خدماتنا
- لإعلامك بالتغييرات
- لتقديم الدعم

## اتصل بنا
drivemaster.se@gmail.com`
    },
    'terms-of-service': {
      en: `# Terms of Service

## 1. Acceptance of Terms
By using DriveMaster, you agree to these Terms of Service.

## 2. Description of Service
DriveMaster provides online driving theory education and practice tests.

## 3. User Accounts
- You must provide accurate information.
- You are responsible for your account security.
- Minimum age: 16 years.

## 4. Payments
- Subscription fees per selected plan.
- Secure payment via Stripe.

## 5. Contact
drivemaster.se@gmail.com`,
      sv: `# Användarvillkor

## 1. Godkännande av villkor
Genom att använda DriveMaster godkänner du dessa villkor.

## 2. Beskrivning av tjänsten
DriveMaster tillhandahåller körkortsteori och övningsprov online.

## 3. Användarkonton
- Du måste ange korrekt information.
- Du ansvarar för kontosäkerheten.
- Minimiålder: 16 år.

## 4. Betalningar
- Avgifter enligt vald plan.
- Säker betalning via Stripe.

## 5. Kontakt
drivemaster.se@gmail.com`,
      ar: `# شروط الخدمة

## 1. قبول الشروط
باستخدام DriveMaster، فإنك توافق على هذه الشروط.

## 2. وصف الخدمة
يوفر DriveMaster تعليم نظرية القيادة واختبارات تدريبية.

## 3. حسابات المستخدمين
- يجب تقديم معلومات دقيقة.
- أنت مسؤول عن أمان حسابك.
- الحد الأدنى للعمر: 16 سنة.

## 4. المدفوعات
- الرسوم حسب الخطة المختارة.
- دفع آمن عبر Stripe.

## 5. اتصل بنا
drivemaster.se@gmail.com`
    },
    'refund-policy': {
      en: `# Refund Policy

## Overview
At DriveMaster, we want you to be completely satisfied with your purchase.

## Subscription Refunds

### Monthly Subscriptions
- Full refund within 7 days if less than 20% content used.

### 6-Month Subscriptions
- Full refund within 14 days if less than 10% content used.
- 50% refund within 30 days.

### Lifetime Access
- Full refund within 14 days if less than 10% content used.

## How to Request
Email drivemaster.se@gmail.com with your account email and reason.

## Contact
drivemaster.se@gmail.com`,
      sv: `# Återbetalningspolicy

## Översikt
På DriveMaster vill vi att du ska vara helt nöjd med ditt köp.

## Prenumerationsåterbetalningar

### Månadsprenumerationer
- Full återbetalning inom 7 dagar om mindre än 20% använts.

### 6-Månaders Prenumerationer
- Full återbetalning inom 14 dagar om mindre än 10% använts.
- 50% återbetalning inom 30 dagar.

### Livstidsåtkomst
- Full återbetalning inom 14 dagar om mindre än 10% använts.

## Hur du begär
Maila drivemaster.se@gmail.com med din e-post och anledning.

## Kontakt
drivemaster.se@gmail.com`,
      ar: `# سياسة الاسترجاع

## نظرة عامة
في DriveMaster، نريدك أن تكون راضياً تماماً عن شرائك.

## استرجاع الاشتراكات

### الاشتراكات الشهرية
- استرجاع كامل خلال 7 أيام إذا استُخدم أقل من 20%.

### اشتراكات 6 أشهر
- استرجاع كامل خلال 14 يوماً إذا استُخدم أقل من 10%.
- استرجاع 50% خلال 30 يوماً.

### الوصول مدى الحياة
- استرجاع كامل خلال 14 يوماً إذا استُخدم أقل من 10%.

## كيفية الطلب
راسل drivemaster.se@gmail.com مع بريدك والسبب.

## اتصل بنا
drivemaster.se@gmail.com`
    },
    'disclaimer': {
      en: `# Disclaimer

## General Information
The information on DriveMaster is for educational purposes only. We make no warranties about its accuracy.

## Not Official Training
DriveMaster is NOT a replacement for official driving school training.

## No Guarantee
We cannot guarantee you will pass the official exam.

## Limitation of Liability
DriveMaster shall not be held liable for any losses arising from use of our service.

## Contact
drivemaster.se@gmail.com`,
      sv: `# Ansvarsfriskrivning

## Allmän Information
Informationen på DriveMaster är endast för utbildningsändamål. Vi ger inga garantier om noggrannhet.

## Inte Officiell Utbildning
DriveMaster ERSÄTTER INTE officiell trafikskola.

## Ingen Garanti
Vi kan inte garantera att du klarar det officiella provet.

## Ansvarsbegränsning
DriveMaster ansvarar inte för förluster från användning av tjänsten.

## Kontakt
drivemaster.se@gmail.com`,
      ar: `# إخلاء المسؤولية

## معلومات عامة
المعلومات على DriveMaster لأغراض تعليمية فقط. لا نقدم ضمانات حول دقتها.

## ليس تدريباً رسمياً
DriveMaster ليس بديلاً عن مدرسة القيادة الرسمية.

## لا ضمان
لا يمكننا ضمان نجاحك في الامتحان الرسمي.

## حدود المسؤولية
لا يتحمل DriveMaster المسؤولية عن أي خسائر من استخدام الخدمة.

## اتصل بنا
drivemaster.se@gmail.com`
    },
    'cookies-policy': {
      en: `# 🍪 Cookies Policy

## What Are Cookies?
Cookies are small text files stored on your device when you visit our website.

## Types of Cookies
- Essential Cookies: Necessary for website function
- Functional Cookies: Remember your preferences
- Analytics Cookies: Help us understand usage

## Managing Cookies
Control cookies through your browser settings.

## Contact
drivemaster.se@gmail.com`,
      sv: `# 🍪 Cookie-policy

## Vad är Cookies?
Cookies är små textfiler som lagras på din enhet när du besöker vår webbplats.

## Typer av Cookies
- Nödvändiga Cookies: Krävs för webbplatsens funktion
- Funktionella Cookies: Kommer ihåg dina preferenser
- Analyscookies: Hjälper oss förstå användning

## Hantera Cookies
Kontrollera cookies via webbläsarens inställningar.

## Kontakt
drivemaster.se@gmail.com`,
      ar: `# 🍪 سياسة ملفات تعريف الارتباط

## ما هي ملفات تعريف الارتباط؟
ملفات تعريف الارتباط هي ملفات نصية صغيرة تُخزن على جهازك عند زيارة موقعنا.

## أنواع ملفات تعريف الارتباط
- ملفات ضرورية: لازمة لعمل الموقع
- ملفات وظيفية: تتذكر تفضيلاتك
- ملفات تحليلية: تساعدنا في فهم الاستخدام

## إدارة ملفات تعريف الارتباط
تحكم في الملفات من إعدادات متصفحك.

## اتصل بنا
drivemaster.se@gmail.com`
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch all pages
      const [privacyRes, termsRes, refundRes, disclaimerRes, cookiesRes] = await Promise.all([
        axios.get(`${API}/pages/privacy-policy`).catch(() => ({ data: null })),
        axios.get(`${API}/pages/terms-of-service`).catch(() => ({ data: null })),
        axios.get(`${API}/pages/refund-policy`).catch(() => ({ data: null })),
        axios.get(`${API}/pages/disclaimer`).catch(() => ({ data: null })),
        axios.get(`${API}/pages/cookies-policy`).catch(() => ({ data: null }))
      ]);

      setContent({
        'privacy-policy': privacyRes.data?.content || defaultContent['privacy-policy'],
        'terms-of-service': termsRes.data?.content || defaultContent['terms-of-service'],
        'refund-policy': refundRes.data?.content || defaultContent['refund-policy'],
        'disclaimer': disclaimerRes.data?.content || defaultContent['disclaimer'],
        'cookies-policy': cookiesRes.data?.content || defaultContent['cookies-policy']
      });
    } catch (error) {
      console.error('Error fetching pages:', error);
      setContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/admin/pages/${activePage}`, {
        content: content[activePage]
      }, { headers });

      toast.success(i18n.language === 'ar' ? 'تم الحفظ بنجاح!' : i18n.language === 'sv' ? 'Sparat!' : 'Saved successfully!');
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error(i18n.language === 'ar' ? 'فشل الحفظ' : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (value) => {
    setContent(prev => ({
      ...prev,
      [activePage]: {
        ...prev[activePage],
        [activeLanguage]: value
      }
    }));
  };

  const handlePreview = () => {
    const urls = {
      'privacy-policy': '/privacy',
      'terms-of-service': '/terms',
      'refund-policy': '/refund',
      'disclaimer': '/disclaimer',
      'cookies-policy': '/cookies'
    };
    window.open(urls[activePage] || '/privacy', '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <FileText className="w-7 h-7 text-purple-500" />
          {i18n.language === 'ar' ? 'إدارة الصفحات القانونية' : i18n.language === 'sv' ? 'Hantera juridiska sidor' : 'Legal Pages Management'}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={handlePreview}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-all"
          >
            <Eye className="w-4 h-4" />
            {i18n.language === 'ar' ? 'معاينة' : i18n.language === 'sv' ? 'Förhandsgranska' : 'Preview'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-all disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving 
              ? (i18n.language === 'ar' ? 'جاري الحفظ...' : 'Saving...') 
              : (i18n.language === 'ar' ? 'حفظ' : i18n.language === 'sv' ? 'Spara' : 'Save')}
          </button>
        </div>
      </div>

      {/* Page Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {i18n.language === 'ar' ? 'اختر الصفحة' : i18n.language === 'sv' ? 'Välj sida' : 'Select Page'}
        </label>
        <div className="flex gap-2">
          {pages.map(page => (
            <button
              key={page.id}
              onClick={() => setActivePage(page.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activePage === page.id
                  ? 'bg-purple-500 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {page.name}
            </button>
          ))}
        </div>
      </div>

      {/* Language Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
          <Globe className="w-4 h-4" />
          {i18n.language === 'ar' ? 'اختر اللغة' : i18n.language === 'sv' ? 'Välj språk' : 'Select Language'}
        </label>
        <div className="flex gap-2">
          {languages.map(lang => (
            <button
              key={lang.code}
              onClick={() => setActiveLanguage(lang.code)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeLanguage === lang.code
                  ? 'bg-emerald-500 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {lang.name}
            </button>
          ))}
        </div>
      </div>

      {/* Content Editor */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {i18n.language === 'ar' ? 'محتوى الصفحة (Markdown)' : i18n.language === 'sv' ? 'Sidinnehåll (Markdown)' : 'Page Content (Markdown)'}
        </label>
        <textarea
          value={content[activePage]?.[activeLanguage] || ''}
          onChange={(e) => handleContentChange(e.target.value)}
          className="w-full h-96 p-4 border border-slate-300 rounded-xl font-mono text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder={i18n.language === 'ar' ? 'أدخل محتوى الصفحة هنا...' : 'Enter page content here...'}
          dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
        />
        <p className="mt-2 text-sm text-slate-500">
          {i18n.language === 'ar' 
            ? 'استخدم # للعناوين الرئيسية، ## للعناوين الفرعية، - للقوائم' 
            : i18n.language === 'sv'
            ? 'Använd # för rubriker, ## för underrubriker, - för listor'
            : 'Use # for headings, ## for subheadings, - for lists'}
        </p>
      </div>
    </div>
  );
};

export default PagesManagement;
